// Continuous choropleth layers (population, density, GDP/capita) — the single
// definition shared by the map paint (`useMapLayers`) and the legend
// (`Legend`). They used to carry their own copy of the metric, which is how
// the legend could describe a scale the map wasn't actually painting.
//
// Each entry carries:
//   expr    – MapLibre expression producing the metric for a feature
//   value   – the same metric in JS, for computing class breaks
//   nodata  – MapLibre predicate: true when the metric is undefined for that
//             feature (no area, no country). Those regions are painted
//             NO_DATA_COLOR instead of the ramp's lowest class, so "no country"
//             never reads as "poorest country".
//   title / fmt – legend heading and value formatting
// (explicit extension: this module is also loaded by plain node in
// tests/unit/js/map_choropleth.test.mjs, which has no Vite resolver)
import { STAT_NEUTRAL } from './colors.js'

export const NO_DATA_COLOR = STAT_NEUTRAL

const fr = (n, digits) => n.toLocaleString('fr-FR', { maximumFractionDigits: digits })

export const CONTINUOUS_LAYERS = {
  population: {
    title: 'Population (habitants)',
    expr: ['coalesce', ['get', 'population'], 0],
    value: (p) => Number(p.population || 0),
    nodata: null,
    fmt: (n) => `${fr(n, 0)} hab.`,
  },
  populationDensity: {
    title: 'Densité (hab/km²)',
    expr: [
      'case',
      ['>', ['coalesce', ['get', 'area_km2'], 0], 0],
      ['/', ['coalesce', ['get', 'population'], 0], ['get', 'area_km2']],
      0,
    ],
    value: (p) => (p.area_km2 > 0 ? (p.population || 0) / p.area_km2 : 0),
    nodata: ['<=', ['coalesce', ['get', 'area_km2'], 0], 0],
    fmt: (n) => `${fr(n, 1)} hab/km²`,
  },
  gdpPerCapita: {
    title: 'PIB / habitant',
    expr: [
      'case',
      ['>', ['coalesce', ['get', 'country_total_population'], 0], 0],
      ['/', ['coalesce', ['get', 'country_gdp'], 0], ['get', 'country_total_population']],
      0,
    ],
    value: (p) => (p.country_total_population > 0 ? (p.country_gdp || 0) / p.country_total_population : 0),
    // More than half the regions belong to no country at all.
    nodata: ['<=', ['coalesce', ['get', 'country_total_population'], 0], 0],
    fmt: (n) => `${fr(n, 0)} €`,
  },
}

export const CONTINUOUS_KEYS = Object.keys(CONTINUOUS_LAYERS)

// The active continuous layer key, or null. Layers are radio-style.
export const activeContinuousKey = (layers) =>
  CONTINUOUS_KEYS.find((k) => layers?.[k]) || null
